import { type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ADMIN_BASE } from '../../utils/hosts';
import { Panel } from '../ui/Panel';
import { Spinner } from '../../ui/Spinner';
import { btnGhost, btnPrimary } from '../ui/styles';
import { money } from '../../core/format/currency';
import { useAdmin } from '../AdminContext';
import { CustomerPicker } from './CustomerPicker';
import { SaleLineRow } from './SaleLineRow';
import { AddLineBar } from './AddLineBar';
import { PaymentBox } from './PaymentBox';
import { SaleSourceAsk } from './SaleSourceAsk';
import { useSaleComposer } from './useSaleComposer';
import type { KassaAppointment, Sale } from '../../core/types/kassa';
type Props = {
  appointment: KassaAppointment | null;
  onDone: (sale: Sale) => void;
  onCancel: () => void;
};

/**
 * Eén bon maken. Vanuit een afspraak staan klant en behandeling er al op (bij een kuursessie op €0);
 * een losse bon begint met de klant. Pas na het antwoord van de server is er afgerekend.
 */
export function SaleComposer({
  appointment,
  onDone,
  onCancel
}: Props) {
  const {
    index
  } = useAdmin();
  const navigate = useNavigate();
  const c = useSaleComposer(appointment);
  const shownProblem = c.touched ? c.problem : null;
  const nothingToPay = c.lines.length > 0 && c.toPayCents === 0;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const sale = await c.pay();
    if (!sale) return;
    const cards = sale.issuedVouchers ?? [];
    const first = cards[0];
    toast.success(`Bon ${sale.number} afgerekend`, {
      description: [sale.changeCents ? `Wisselgeld ${money(sale.changeCents)}.` : '', sale.lines.some(l => l.kind === 'kuur') ? 'De kuur staat in haar dossier en in Mijn omgeving.' : '', cards.length ? `${cards.length === 1 ? 'Cadeaubon' : `${cards.length} cadeaubonnen`} klaar: ${cards.map(v => `…${v.last4}`).join(', ')}.` : ''].filter(Boolean).join(' ') || undefined,
      // Meteen door naar de bon om hem te printen; bij meer bonnen staan ze allemaal in Cadeaubonnen.
      ...(first ? {
        action: {
          label: cards.length === 1 ? 'Bon printen' : 'Naar cadeaubonnen',
          onClick: () => navigate(cards.length === 1 ? `${ADMIN_BASE}/cadeaubonnen/${first.id}` : `${ADMIN_BASE}/cadeaubonnen`)
        },
        duration: 10000
      } : {})
    });
    onDone(sale);
  };
  return <form onSubmit={e => void submit(e)} noValidate className="space-y-6" data-id="element-2360" data-mp="13iltvv26zpae">
      <Panel title={appointment ? `Afrekenen · ${appointment.customerName}` : 'Losse bon'} description={appointment ? `${index.treatmentBySlug(appointment.treatmentSlug)?.name ?? appointment.treatmentSlug} om ${appointment.time}` : 'Voor een product aan de balie, een behandeling zonder afspraak, een kuur of een cadeaubon.'} actions={<button type="button" onClick={onCancel} disabled={c.submitting} className={btnGhost} data-id="element-2362" data-mp="3f96e3cugg1w7">
            Niet afrekenen
          </button>} data-id="element-2361" data-mp="13ghrxinut0cr">
        {!appointment && <div className="mb-5" data-id="element-2363" data-mp="1psc7tgimqu0k">
            <CustomerPicker value={c.customer} onChange={c.setCustomer} data-id="element-2364" data-mp="1pp6fztim9e15" />
            {c.askSource && <SaleSourceAsk value={c.source} onChange={c.setSource} data-id="element-2365" data-mp="1uqlz3g18hagw" />}
          </div>}

        {c.lines.length === 0 ? <p className="rounded-md bg-shell px-4 py-6 text-center text-[13px] text-muted" data-id="element-2366" data-mp="23inz5d7e4oqn">Nog niets op de bon. Kies hieronder wat ze afrekent.</p> : <ul className="divide-y divide-line border-y border-line" aria-label="Regels op de bon" data-id="element-2367" data-mp="28h8xt86zv0cz">
            {c.lines.map(l => <SaleLineRow key={l.key} line={l} c={c} data-id="element-2368" data-mp="2ifyi17md4wwe" />)}
          </ul>}

        <div className="mt-5" data-id="element-2369" data-mp="2ie3tljbvumnk">
          <AddLineBar c={c} data-id="element-2370" data-mp="2mhtik9wgz694" />
        </div>
      </Panel>

      <Panel title="Betalen" data-id="element-2371" data-mp="2mf7ckep88yt0">
        <PaymentBox c={c} data-id="element-2372" data-mp="o2fdvextug24" />
      </Panel>

      {(shownProblem || c.error) && <p role="alert" className="rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink" data-id="element-2373" data-mp="s3iva5viwk5g">
          {c.error ?? shownProblem}
        </p>}

      <div className="flex flex-wrap items-center gap-3" data-id="element-2374" data-mp="3sxf6is3dfcz3">
        <button type="submit" disabled={c.submitting} aria-busy={c.submitting} className={btnPrimary} data-id="element-2375" data-mp="3oxesh8k6p3sk">
          {c.submitting && <Spinner data-id="element-2376" data-mp="6rs3i1l0btbh" />}
          {c.submitting ? 'Afrekenen…' : nothingToPay ? 'Bon maken (niets te betalen)' : `Afrekenen ${money(c.toPayCents)}`}
        </button>
        <span className="text-[12px] text-muted" data-id="element-2377" data-mp="1qm3rjsxj5sg">De bon telt meteen mee in de dag en in Omzet.</span>
      </div>
    </form>;
}